'use client'

// imports
import { useEffect, useState } from 'react';

// lib files
import { handleRecommendedShowClick } from '@/lib/content-services/rec-engine';
import safeFetch from '@/lib/helpers/safe-fetch';
import { sendPbsGtmEvent } from '@/lib/helpers/send-pbs-gtm-event';
import { useIdentity } from '@/lib/hooks/useIdentity';

// types
import { ProfileData } from '@/lib/types/api/profile-data';
import type { ShowBadgeFlags } from '@/components/ShowPoster/ShowPoster';
import { ShowRowContent } from "@/lib/types/api/show-data";

// constants
import { RECOMMENDED_SHOWS_FEATURE_CATEGORY } from '@/lib/constants';

// components
import ShowRow from "@/components/ShowRow/ShowRow";


interface MustWatchOrRecommendedDramasRowProps {
  profileStub?: ProfileData;
  depRecommendedDramas?: ShowRowContent[];
  mustWatchDramas: ShowRowContent[];
  showBadgeFlags?: ShowBadgeFlags;
}

export const MustWatchOrRecommendedDramasRow = ({
  mustWatchDramas,
  showBadgeFlags,
}: MustWatchOrRecommendedDramasRowProps) => {
  const { isAuthenticated, cachedIdentity } = useIdentity();
  const [recommendedDramas, setRecommendedDramas] = useState<ShowRowContent[] | null>(null);
  const userFirstName = cachedIdentity?.firstName;
  useEffect(() => {
    if (recommendedDramas) return;

    const fetchRecommended = async () => {
      try {
        const response = await safeFetch('/api/profile/recommended-shows/?genre=drama');
        const json = await response.json();
        const recommendedDramasData = json.recommendedShowsData;
        setRecommendedDramas(recommendedDramasData);
      } catch (error) {
        console.error(error);
      }
    };

    if (isAuthenticated) {
      fetchRecommended();
    }
  }, [isAuthenticated, recommendedDramas]);

  const handleRecommendedShowPosterClick = (show: ShowRowContent, index?: number, linkHref?: string) => {
    const { title, slug } = show;
    handleRecommendedShowClick(show);
    sendPbsGtmEvent({
      show_title: title,
      pbs_content_id: slug,
      object_name: "show poster",
      object_type: "link",
      object_position: index !== undefined ? index + 1 : undefined,
      object_location: `drama explore hub recommended show row`,
      object_action: "click",
      object_action_behavior: "user navigates to show page",
      object_url: linkHref,
      feature_name: "recommended show poster",
      feature_category: RECOMMENDED_SHOWS_FEATURE_CATEGORY,
      signed_in_status: true,
    });
  }

  // if the user is logged in and the recommendedDramas are available, show them
  if (isAuthenticated && recommendedDramas && recommendedDramas.length > 0) {
    return (
      <ShowRow
        shows={recommendedDramas}
        onShowPosterClick={handleRecommendedShowPosterClick}
        title={`Top Picks for ${userFirstName ? userFirstName : "You"}`}
        showBadgeFlags={showBadgeFlags}
      />);
  }
  // otherwise, show the must watch dramas
  else if (mustWatchDramas && mustWatchDramas.length > 0) {
    return <ShowRow shows={mustWatchDramas} title="Must Watch Dramas" showBadgeFlags={showBadgeFlags} />;
  }

  return null;
};
